import { Col, Row } from 'antd'
import React, { useEffect, useState } from 'react'
import {
  Controller,
  useController,
  useFormContext,
  useWatch,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { InputField } from '@shared/InputField'
import RadioButtonGroup from '@shared/RadioButtonGroup'
import { SearchOutlined, CloseOutlined } from '@ant-design/icons'
import { useDebounceCallback } from '@shared/hooks/useDebounceCallback'
import { orderStatusListType } from 'store/types/salaryCard'
import { secureLocalStorageGet } from '../../../../../helpers'
import {
  BusinessCardListRequest,
  StatusType,
  useLazyBusinessCardListQuery,
  useLazyBusinessCardStatusListQuery,
} from '@app/store/reducers/salaryCards/businessCardOrder.api'
import { useFetchBusinessDateMutation } from '@app/store/reducers/configs/businessDate.api'
import FilterDateInputs from '@shared/FilterDateInput/FilterDateInputs'

function getDefaultStatus(options: orderStatusListType[]) {
  const role = secureLocalStorageGet('rol')

  const MUST_BE_SIGNED = options.find(
    (i) => i.status === StatusType.PENDING_SIGNER
  )
  const CLOSED = options.find((i) => i.status === StatusType.CLOSED)

  if (CLOSED) return StatusType.CLOSED
  if (MUST_BE_SIGNED && role === 2) return StatusType.FOR_MUST_BE_SIGNED

  return options?.[0]?.status
}

function BusinessCardFilterBar() {
  const { t } = useTranslation()

  const [localOrderStatusList, setLocalOrderStatusList] = useState<
    orderStatusListType[]
  >([])

  const [
    fetchSalaryCardOrderstatus,
    { isSuccess, isFetching, isLoading, data: orderStatusList = [] },
  ] = useLazyBusinessCardStatusListQuery()

  const [mutate] = useLazyBusinessCardListQuery()

  const {
    setValue,
    getValues,
    resetField,
    control,
    trigger,
    formState: { isSubmitting },
  } = useFormContext<BusinessCardListRequest>()

  const {
    fieldState: { invalid: startDateInValid, error: startError },
  } = useController({ control, name: 'startDate' })
  const {
    fieldState: { invalid: endDateInValid, error: endError },
  } = useController({ control, name: 'endDate' })

  const debounced = useDebounceCallback((e) => {
    setValue('searchText', e)
  }, 500)

  const startDate = useWatch({
    control,
    name: 'startDate',
  })
  const endDate = useWatch({
    control,
    name: 'endDate',
  })
  const searchText = useWatch({ control, name: 'searchText' })
  const status = useWatch({ control, name: 'status' })

  useEffect(() => {
    ;(async () => {
      const isValid = await trigger(['startDate', 'endDate'])
      setValue('status', '', { shouldValidate: true })

      if (isValid) {
        fetchSalaryCardOrderstatus({
          searchText,
          startDate,
          endDate,
        })
      }
    })()
  }, [startDate, endDate])

  useEffect(() => {
    if (isSuccess && !isFetching) {
      setLocalOrderStatusList(orderStatusList)

      const defaultStatus = getDefaultStatus(orderStatusList)
      setValue('status', defaultStatus, { shouldValidate: true })
    }
  }, [isFetching, isSuccess])

  useEffect(() => {
    ;(async () => {
      const isValid = await trigger()
      const data = getValues()

      if (isValid) {
        const reqData = {
          ...data,
          status:
            data.status === StatusType.FOR_MUST_BE_SIGNED
              ? StatusType.PENDING_SIGNER
              : data.status,
        }

        mutate(reqData)
      }
    })()
  }, [searchText, status])

  const [fetchBusinessDate] = useFetchBusinessDateMutation({
    fixedCacheKey: 'businessDate',
  })

  useEffect(() => {
    fetchBusinessDate()
  }, [])

  return (
    <fieldset disabled={isSubmitting}>
      <Row style={{ flexDirection: 'column', gap: 8 }}>
        <Col span={24}>
          <Row wrap={true} justify="space-between">
            <Col span={12}>
              <FilterDateInputs
                startDateError={startDateInValid}
                startDateErrorMessage={startError?.message}
                endDateError={endDateInValid}
                endDateErrorMessage={endError?.message}
                value="thisMonth"
                onChangePicker={(value) => {
                  setValue(
                    'startDate',
                    value.dateFrom?.format('DD.MM.YYYY') as string,
                    {
                      shouldValidate: true,
                    }
                  )
                  setValue(
                    'endDate',
                    value.dateTo?.format('DD.MM.YYYY') as string,
                    {
                      shouldValidate: true,
                    }
                  )
                }}
              />
            </Col>
            <Col span={6}>
              <Controller
                control={control}
                name="searchText"
                render={({
                  field: { onChange, value, ...fields },
                  fieldState: { isDirty },
                }) => {
                  return (
                    <InputField
                      placeholder={t(
                        'orders.salaryCardOrder.inputLabels.minimalSymbol'
                      )}
                      label={t('orders.salaryCardOrder.inputLabels.search')}
                      wrapperCol={{ span: 16 }}
                      labelCol={{ span: 8 }}
                      suffix={
                        !isDirty ? (
                          <span className="anticon anticon-eye-invisible ant-input-password-icon">
                            <SearchOutlined />
                          </span>
                        ) : (
                          <span
                            onClick={() => resetField('searchText')}
                            className="anticon anticon-eye-invisible ant-input-password-icon ant-typography ant-typography-danger"
                          >
                            <CloseOutlined />
                          </span>
                        )
                      }
                      onChange={(value) => debounced(value)}
                    />
                  )
                }}
              />
            </Col>
            {/* <Col span={6}>
              <FilterActionButtons />
            </Col> */}
          </Row>
        </Col>
        <Col span={24}>
          <Row justify="space-between">
            <Col>
              <Controller
                control={control}
                name="status"
                render={({ field }) => (
                  <RadioButtonGroup
                    options={localOrderStatusList}
                    label="Status"
                    loading={isLoading}
                    {...field}
                    customTranslationKey='businessCardOrders.buttonsLabels.RadioButtonGroup'
                  />
                )}
              />
            </Col>
          </Row>
        </Col>
      </Row>
    </fieldset>
  )
}

export default BusinessCardFilterBar
